import React, { useEffect, useState } from 'react';
import { HiArrowUpRight, HiCheck, HiCheckCircle, HiClipboard, HiClock, HiExclamationTriangle } from 'react-icons/hi2';
import Link from './resource-link';
import { chatCard, chatIconTile } from './chat-theme';
import { CHANNELS } from '../model/publishing';

const STATUS = {
    published: { icon: HiCheckCircle, title: 'Your agent is live', tone: 'success' },
    review_requested: { icon: HiClock, title: 'Waiting for approval', tone: 'decision' },
    failed: { icon: HiExclamationTriangle, title: 'Not published', tone: 'danger' },
};
const pill = 'inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[0.8rem] font-medium text-gray-800 transition-colors hover:border-gray-300 hover:bg-gray-50';

function CopySnippet({ snippet }) {
    const [copied, setCopied] = useState(false);
    useEffect(() => {
        if (!copied) return undefined;
        const timer = setTimeout(() => setCopied(false), 1500);
        return () => clearTimeout(timer);
    }, [copied]);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(snippet);
            setCopied(true);
        } catch (_) { /* clipboard blocked: the code stays selectable */ }
    };
    return (
        <div className="relative">
            <pre aria-label="Website snippet" className="overflow-x-auto rounded-xl bg-[#111827] p-3 pr-20 text-xs leading-5 text-fixed-white/90 dark:bg-fixed-black/50"><code>{snippet}</code></pre>
            <button type="button" onClick={copy} className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-lg bg-fixed-white/10 px-2 py-1 text-[11px] font-medium text-fixed-white hover:bg-fixed-white/20">
                {copied ? <><HiCheck className="h-3.5 w-3.5" /> Copied</> : <><HiClipboard className="h-3.5 w-3.5" /> Copy</>}
            </button>
        </div>
    );
}

/** The outcome of "Publish my agent": status, the website snippet, and every channel's setup page. */
export default function PublishCard({ result, appId }) {
    const { icon: Icon, title, tone } = STATUS[result.status];
    const base = appId ? `/app/${encodeURIComponent(appId)}` : null;
    return (
        <section aria-label="Publish result" className={`${chatCard(result.status === 'failed' ? 'danger' : 'planning')} space-y-4 text-[0.875rem]`}>
            <p className="flex items-center gap-2.5 font-semibold text-gray-900">
                <span className={chatIconTile(tone)}><Icon className="h-4 w-4" /></span> {title}
                {result.versionName && <span className="font-normal text-gray-500">· {result.versionName}</span>}
            </p>
            {result.snippet && (
                <div className="space-y-2">
                    <p className="font-medium text-gray-900">Add it to your website</p>
                    <p className="text-[0.8rem] text-gray-500">
                        Paste this just before the closing <code>&lt;/body&gt;</code> tag on every page where visitors should see your assistant.
                        It includes a new Live access key.
                    </p>
                    <CopySnippet snippet={result.snippet} />
                </div>
            )}
            {base && (
                <div className="space-y-2">
                    <p className="font-medium text-gray-900">{result.snippet ? 'Or connect another channel' : 'Channels'}</p>
                    <div className="flex flex-wrap gap-2">
                        {CHANNELS.map(channel => (
                            <Link key={channel.id} to={`${base}${channel.path}`} className={pill}>
                                {channel.label} <HiArrowUpRight className="h-3 w-3" />
                            </Link>
                        ))}
                    </div>
                    <p className="text-[0.8rem] text-gray-500">
                        Manage <Link to={`${base}/versions`} className="underline">versions</Link> and <Link to={`${base}/access-keys`} className="underline">access keys</Link>.
                    </p>
                </div>
            )}
        </section>
    );
}
